import { useCallback, useState, useEffect, useRef } from 'react';

export const storageUtilFactory = (storage: Storage) => {
  const setItem = <T extends unknown>(key: string, data: T) => {
    storage.setItem(key, JSON.stringify(data));
  };

  const getItem = <TData = unknown>(key: string, defaultValue?: TData): TData | undefined => {
    const value = storage.getItem(key);
    return value !== null ? JSON.parse(value) as TData : defaultValue;
  };

  const removeItem = (key: string) => storage.removeItem(key);
  const clear = () => storage.clear();

  return {
    setItem,
    getItem,
    removeItem,
    clear,
  };
};

export const localStorageService = storageUtilFactory(window.localStorage);
export const sessionStorageService = storageUtilFactory(window.sessionStorage);

const usePrevious = (value:any) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};
const useHasChanged= (val: any) => {
  const prevVal = usePrevious(val);
  return prevVal !== val;
};
const useStorageFactory = (storage: ReturnType<typeof storageUtilFactory>) =>
  <T>(key: string, initialValue?: T) => {
    const [value, setStoredValue] = useState(() => storage.getItem(key, initialValue));
    // Use a useEffect to watch for changes in the key
    const hasKeyChanged = useHasChanged(key);

    useEffect(() => {
      if (hasKeyChanged) {
      // When the key changes, update the value
        setStoredValue(storage.getItem(key, initialValue));
      }
    }, [key, initialValue, hasKeyChanged]);


    const setValue = useCallback((newValue: T): void => {
      setStoredValue(newValue);
      storage.setItem(key, newValue);
    }, [setStoredValue, key]);

    return [value, setValue] as [T, (value: T) => void];
  };


export const useLocalStorage = useStorageFactory(localStorageService);
export const useSessionStorage = useStorageFactory(sessionStorageService);
